<template>
  <v-container fluid>
    <page-header showBackButton></page-header>

    <v-card class="mb-8">
      <v-card-text>
        <v-form ref="basicForm" @submit.prevent>
          <v-row class="ma-0">
            <v-col cols="12">
              <image-uploader
                :fileValue.sync="formData.thumbnail"
                label="uploadImage"
                fileType="image"
                inputRef="uploadThumbnailFile"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <form-select label="status" :fieldValue.sync="formData.status" :options="statusOptions" required />
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <form-date-picker
                label="createDate"
                :fieldValue.sync="formData.post_date"
                required
                manualInput
                disabledActionButtons
              />
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <form-input
                label="URL"
                :fieldValue.sync="formData.url"
                required
                prefix="/blog/"
                :customRules="[v => $validate.regex('blogSlug', v) || $t('form.blogSlugInv')]"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <form-input label="headingZh" :fieldValue.sync="formData.title_zh" required />
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <form-input label="headingEn" :fieldValue.sync="formData.title_en" required />
            </v-col>
            <v-col cols="12">
              <form-input label="Meta Description" :fieldValue.sync="formData.meta_description" />
            </v-col>
            <v-col cols="12" class="my-4">
              <v-tabs class="mb-2" v-model="contentTab" :height="40" active-class="tab-active" hide-slider>
                <v-tab :disabled="$store.getters.isLoading">{{ $t('contentZh') }}</v-tab>
                <v-tab :disabled="$store.getters.isLoading">{{ $t('contentEn') }}</v-tab>
              </v-tabs>
              <v-tabs-items v-model="contentTab">
                <v-tab-item transition="fade-transition">
                  <quill-editor :html.sync="formData.content_zh" editorID="editorContentZh" />
                </v-tab-item>
                <v-tab-item transition="fade-transition">
                  <quill-editor :html.sync="formData.content_en" editorID="editorContentEn" />
                </v-tab-item>
              </v-tabs-items>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-btn class="mt-2" :loading="$store.getters.isLoading" color="primary" @click="save()">{{ $t('save') }}</v-btn>
  </v-container>
</template>

<script>
import FormInput from '@/components/form/FormInput.vue'
import FormSelect from '@/components/form/FormSelect.vue'
import ImageUploader from '@/components/form/ImageUploader.vue'
import QuillEditor from '@/components/form/QuillEditor.vue'
import FormDatePicker from '@/components/form/FormDatePicker.vue'

export default {
  name: 'BlogDetail',
  components: {
    FormInput,
    FormSelect,
    ImageUploader,
    QuillEditor,
    FormDatePicker,
  },
  computed: {
    id() {
      const id = parseInt(this.$route.params.id)
      if (!isNaN(id)) {
        return id
      }

      return null
    },
  },
  data: vm => ({
    contentTab: 0,
    formData: {
      post_date: '',
      url: '',
      meta_description: '',
      title_zh: '',
      title_en: '',
      thumbnail: '',
      content_zh: '',
      content_en: '',
      status: 'active',
    },

    statusOptions: [
      { value: 'active', text: vm.$t('blogStatusOptions.active') },
      { value: 'quit', text: vm.$t('blogStatusOptions.quit') },
    ],
  }),
  methods: {
    async getBlog() {
      try {
        const data = await this.$Fetcher.GetBlogById(this.id)
        for (let key in this.formData) {
          if (this.$validate.DataValid(data[key])) {
            this.formData[key] = data[key]
          }
        }
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setAlert',{
          message: 'noData',
          redirect: 'BlogList',
        })
      } finally {
        this.$store.dispatch('setInitLoading', false)
      }
    },

    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const valid = await this.$refs['basicForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', {
          message: 'required',
        })
        return
      }

      this.$store.dispatch('setLoading', true)
      let payload = {
        status: this.formData.status,
        title_zh: this.formData.title_zh,
        title_en: this.formData.title_en,
        post_date: this.formData.post_date,
        url: this.formData.url,
        meta_description: this.formData.meta_description,
        thumbnail: this.formData.thumbnail,
        content_zh: this.formData.content_zh,
        content_en: this.formData.content_en,
      }

      if (this.$validate.DataValid(this.id)) {
        try {
          await this.$Fetcher.UpdateBlog(this.id, payload)
          this.$store.dispatch('setDataIsUpdated', false)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('toggleSnack', {
            type: 'success',
            message: 'message.saveSuccess',
            refresh: true,
          })
        } catch (err) {
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setAlert',{
            title: 'message.saveFail',
            message: err,
            type: 'error',
          })
        }
      } else {
        try {
          await this.$Fetcher.NewBlog(payload)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setDataIsUpdated', false)
          this.$store.dispatch('toggleSnack', {
            type: 'success',
            message: 'message.saveSuccess',
            redirect: 'BlogList',
          })
        } catch (err) {
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setAlert',{
            title: 'message.saveFail',
            message: err,
            type: 'error',
          })
        }
      }
    },
  },

  async mounted() {
    this.$store.dispatch('setInitLoading', true)
    if (this.$validate.DataValid(this.id)) {
      // edit
      this.getBlog()
    } else {
      // new
      this.$store.dispatch('setInitLoading', false)
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-tabs {
  box-shadow: none !important;
}
</style>
